<!-- components/ContactUs.vue -->
<template>
  <section id="contact" class="bg-white py-16 px-4 sm:px-6 lg:px-8">
    <div class="max-w-6xl mx-auto">
      <div class="text-center mb-12">
        <h2 class="text-3xl font-bold text-gray-900 sm:text-4xl">Contact Us</h2>
        <p class="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
          Whether you have a question about our services, need a quote, or want to discuss your project, our team is ready to assist you. We aim to respond to all inquiries within 48 hours during business days.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
        <!-- Contact Form -->
        <div class="bg-gray-50 rounded-lg p-8 shadow-sm">
          <h3 class="text-xl font-semibold text-gray-900 mb-6">Send Us a Message</h3>

          <!-- Success Message -->
          <div v-if="formStatus === 'success'" class="p-4 mb-4 text-sm rounded-lg bg-green-100 text-green-800">
            Thank you! Your message has been sent successfully.
          </div>

          <!-- Error Message -->
          <div v-if="formStatus === 'error'" class="p-4 mb-4 text-sm rounded-lg bg-red-100 text-red-800">
            Something went wrong. Please try again later.
          </div>

          <!-- Static HTML form for Netlify's form detection - hidden in production -->
          <form name="contact" netlify netlify-honeypot="bot-field" hidden>
            <input type="text" name="name" />
            <input type="email" name="email" />
            <textarea name="message"></textarea>
            <input type="text" name="company" />
            <input type="tel" name="phone" />
            <input type="text" name="subject" />
            <input type="checkbox" name="human-check" />
            <input type="hidden" name="timestamp" />
            <input type="hidden" name="form-submission-time" />
          </form>

          <!-- Actual Form -->
          <form
            ref="contactForm"
            class="space-y-6"
            name="contact"
            method="POST"
            @submit.prevent="handleSubmit"
          >
            <!-- Hidden field for Netlify form name -->
            <input type="hidden" name="form-name" value="contact" />

            <!-- Honeypot for spam prevention -->
            <p class="hidden">
              <label>Don't fill this out if you're human: <input name="bot-field" /></label>
            </p>

            <div>
              <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                v-model="formData.name"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Your name"
                autocomplete="name"
              />
            </div>

            <div>
              <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                v-model="formData.email"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="your.email@example.com"
                autocomplete="email"
              />
            </div>

            <div>
              <label for="company" class="block text-sm font-medium text-gray-700">Company (Optional)</label>
              <input
                type="text"
                id="company"
                name="company"
                v-model="formData.company"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Your company"
                autocomplete="organization"
              />
            </div>

            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700">Phone (Optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                v-model="formData.phone"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Your phone number"
                autocomplete="tel"
              />
            </div>

            <div>
              <label for="subject" class="block text-sm font-medium text-gray-700">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                v-model="formData.subject"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Subject of your message"
              />
            </div>

            <div>
              <label for="message" class="block text-sm font-medium text-gray-700">Message</label>
              <textarea
                id="message"
                name="message"
                v-model="formData.message"
                rows="4"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="How can we help you?"
              ></textarea>
            </div>

            <!-- User interaction verification (helps prevent spam) -->
            <div>
              <div class="flex items-center">
                <input
                  id="human-check"
                  name="human-check"
                  type="checkbox"
                  v-model="formData.humanCheck"
                  required
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label for="human-check" class="ml-2 block text-sm text-gray-700">
                  I confirm I am a human
                </label>
              </div>
            </div>

            <!-- Hidden timestamp for anti-spam -->
            <input type="hidden" name="timestamp" :value="timestamp" />

            <div>
              <button
                type="submit"
                :disabled="formStatus === 'submitting' || !formData.humanCheck"
                class="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="formStatus === 'submitting'" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ formStatus === 'submitting' ? 'Submitting...' : 'Submit' }}
              </button>
            </div>
          </form>
        </div>

        <!-- Contact Information -->
        <div>
          <h3 class="text-xl font-semibold text-gray-900 mb-6">Get in Touch</h3>

          <div class="space-y-6">
            <div v-for="(contact, index) in contacts" :key="index" class="flex items-start">
              <div class="flex-shrink-0">
                <component :is="contact.icon" class="h-6 w-6 text-blue-500" aria-hidden="true" />
              </div>
              <div class="ml-3 text-base text-gray-700">{{ contact.value }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { EnvelopeIcon, PhoneIcon } from '@heroicons/vue/24/outline'

export default {
  name: 'ContactUs',
  components: {
    EnvelopeIcon,
    PhoneIcon
  },
  data() {
    return {
      contacts: [
        {
          value: 'enquiries@fordjames.co.uk',
          icon: EnvelopeIcon,
        },
        {
          value: 'Rob - 07538 506 489',
          icon: PhoneIcon,
        },
        {
          value: 'Tom - 07768 686 369',
          icon: PhoneIcon,
        }
      ],
      formData: {
        name: '',
        email: '',
        message: '',
        company: '',
        phone: '',
        subject: '',
        humanCheck: false
      },
      timestamp: Date.now().toString(),
      formStatus: null, // null, 'submitting', 'success', 'error'
      submitTime: 0
    }
  },
  mounted() {
    // Track when the form was loaded to check for submissions that are too quick
    this.submitTime = Date.now()
  },
  methods: {
    handleSubmit() {
      // Check if the form was submitted too quickly (likely a bot)
      const timeElapsed = Date.now() - this.submitTime
      if (timeElapsed < 3000) { // Less than 3 seconds
        console.log('Form submitted too quickly, likely a bot')
        // Pretend to submit but don't actually submit
        this.formStatus = 'submitting'

        setTimeout(() => {
          this.formStatus = 'success'
          this.resetForm()
        }, 1500)

        return
      }

      // Set form to submitting state
      this.formStatus = 'submitting'

      // Create a manual data object to ensure all fields are present
      const submissionData = new FormData()

      // Add form name (required by Netlify)
      submissionData.append('form-name', 'contact')

      // Add core fields
      submissionData.append('name', this.formData.name)
      submissionData.append('email', this.formData.email)
      submissionData.append('message', this.formData.message)

      // Add additional fields
      submissionData.append('subject', this.formData.subject || '')
      submissionData.append('company', this.formData.company || 'Not provided')
      submissionData.append('phone', this.formData.phone || 'Not provided')

      // Add human check
      submissionData.append('human-check', this.formData.humanCheck ? 'yes' : 'no')

      // Add anti-spam fields
      submissionData.append('timestamp', this.timestamp)
      submissionData.append('form-submission-time', timeElapsed.toString() + 'ms')

      // Convert FormData to URL encoded string that Netlify expects
      const urlEncodedData = new URLSearchParams(submissionData).toString()

      // Debug - log the data being sent
      console.log('Submitting form with data:', Object.fromEntries(submissionData.entries()))

      // Submit form to Netlify
      fetch('/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Form submission failed: ${response.status}`)
          }
          return response
        })
        .then(() => {
          console.log('Form submitted successfully')

          // Form submitted successfully
          this.formStatus = 'success'

          // Reset form
          this.resetForm()
        })
        .catch(error => {
          console.error('Form submission error:', error)
          this.formStatus = 'error'

          // Reset form status after 5 seconds
          setTimeout(() => {
            this.formStatus = null
          }, 5000)
        })
    },

    resetForm() {
      // Reset form fields
      this.formData = {
        name: '',
        email: '',
        message: '',
        company: '',
        phone: '',
        subject: '',
        humanCheck: false
      }

      // Generate a new timestamp
      this.timestamp = Date.now().toString()

      // Reset form status after 5 seconds
      setTimeout(() => {
        this.formStatus = null
      }, 5000)
    }
  }
}
</script>